import { KeyRound, Plus, Trash2 } from 'lucide-react';

/**
 * Generic shape shared by the admin and tenant provider forms. Both `features/admin` and
 * `features/config` export their own `ProviderHeaderInput`; the editor accepts the structural
 * type instead of importing a specific module so it stays decoupled from either feature.
 */
export interface CustomHeaderEntry {
  name: string;
  value: string;
  isSecret: boolean;
}

interface CustomHeadersEditorProps {
  value: CustomHeaderEntry[];
  busy: boolean;
  onChange: (next: CustomHeaderEntry[]) => void;
  /** Existing entries (for edit mode) that already have their secret stored in Key Vault.
   *  When provided, secret rows whose `value` matches a known KV ref render a placeholder
   *  ("•••• stored in KV") and require the operator to click "Rotar" before they can type a new value. */
  storedSecretRefs?: ReadonlySet<string>;
  maxEntries?: number;
}

const DEFAULT_MAX = 16;

/**
 * Editor for the free-form HTTP headers attached to a provider. Used by both the admin
 * `PlatformProviderForm` and the tenant `EditProviderModal` so the experience stays uniform.
 *
 * Why the secret/value split exists in the UI: secret headers are stored in Key Vault, so the
 * backend never returns their plaintext on read. The editor must therefore distinguish "I am
 * typing a brand-new secret" from "this row already has a secret stored — leave it alone". The
 * `storedSecretRefs` set tells the editor which rows are in the latter state.
 */
export function CustomHeadersEditor({
  value,
  busy,
  onChange,
  storedSecretRefs,
  maxEntries = DEFAULT_MAX,
}: CustomHeadersEditorProps): JSX.Element {
  const update = (index: number, patch: Partial<CustomHeaderEntry>): void => {
    const next = value.map((entry, i) => (i === index ? { ...entry, ...patch } : entry));
    onChange(next);
  };

  const remove = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const add = (): void => {
    if (value.length >= maxEntries) return;
    onChange([...value, { name: '', value: '', isSecret: false }]);
  };

  return (
    <div className="rounded-xl border border-slate-200/70 bg-slate-50/40 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-800">
          Cabeceras personalizadas
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({value.length}/{maxEntries})
          </span>
        </p>
        <button
          type="button"
          onClick={add}
          disabled={busy || value.length >= maxEntries}
          className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-medium text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} /> Añadir
        </button>
      </div>

      {value.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          Define cabeceras adicionales para autenticación tipo Bearer, proxies con
          autorización propia o cabeceras de bypass (p.ej. <code>ngrok-skip-browser-warning</code>).
          Marca <em>secreto</em> para guardar el valor en Key Vault.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {value.map((entry, index) => {
            const isStoredSecret = entry.isSecret && storedSecretRefs?.has(entry.value) === true;
            return (
              <li
                key={index}
                className="grid grid-cols-1 items-start gap-2 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-[1fr_1fr_auto_auto]"
              >
                <input
                  className="input"
                  placeholder="Header"
                  value={entry.name}
                  onChange={(e) => update(index, { name: e.target.value })}
                  disabled={busy}
                  spellCheck={false}
                  autoComplete="off"
                />
                <input
                  className="input"
                  placeholder={isStoredSecret ? '•••• almacenado en KV (haz clic en "Rotar")' : entry.isSecret ? 'Valor (se guardará en KV)' : 'Valor literal'}
                  value={isStoredSecret ? '' : entry.value}
                  onChange={(e) => update(index, { value: e.target.value })}
                  disabled={busy || isStoredSecret}
                  type={entry.isSecret ? 'password' : 'text'}
                  autoComplete="new-password"
                  spellCheck={false}
                />
                <label
                  className="inline-flex items-center gap-1 self-center text-xs text-slate-700"
                  title="Si lo marcas, el valor se guarda en Key Vault y nunca se persiste en Cosmos."
                >
                  <input
                    type="checkbox"
                    checked={entry.isSecret}
                    onChange={(e) => update(index, { isSecret: e.target.checked, value: '' })}
                    disabled={busy}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-violet-600"
                  />
                  <KeyRound className="h-3.5 w-3.5 text-slate-500" aria-hidden strokeWidth={2.25} />
                  secreto
                </label>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={busy}
                  className="self-center rounded-md p-1 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Eliminar"
                >
                  <Trash2 className="h-4 w-4" aria-hidden strokeWidth={2.25} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
